<template>
  <button
    type="button"
    class="card"
    :class="[
      'card--' + item.rarity,
      {
        'card--owned': item.owned,
        'card--equipped': item.equipped,
        'card--selected': selected,
      },
    ]"
    @click="$emit('select', item)"
  >
    <div v-if="item.isNew" class="card__badge card__badge--new">NEW</div>
    <div v-if="item.equipped" class="card__badge card__badge--worn">{{ item.category === 'room' ? 'В КОМНАТЕ' : 'НАДЕТО' }}</div>
    <div v-else-if="item.owned" class="card__badge card__badge--owned">✓</div>

    <ShopItemThumbnail :item-id="item.id" :title="item.title" />

    <div class="card__title" :title="item.title">{{ item.title }}</div>

    <div class="card__footer">
      <template v-if="item.owned">
        <span class="card__status" :class="item.equipped ? 'card__status--equipped' : 'card__status--owned'">
          {{ item.equipped ? (item.category === 'room' ? "Размещено" : "Надето") : "Куплено" }}
        </span>
      </template>
      <template v-else>
        <span class="card__price" :class="{ 'card__price--lock': !canAfford }">
          {{ item.price }} монет
        </span>
      </template>
    </div>
  </button>
</template>

<script setup lang="ts">
import { computed } from "vue"
import type { ShopItemDto } from "../shared/shopTypes"
import ShopItemThumbnail from "./ShopItemThumbnail.vue"

const props = defineProps<{
  item: ShopItemDto
  coins: number
  selected: boolean
}>()

defineEmits<{
  select: [item: ShopItemDto]
}>()

const canAfford = computed(() => props.coins >= props.item.price)
</script>

<style scoped>
.card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 14px 10px 10px;
  border-radius: 16px;
  background: #fff;
  border: 2px solid #ECE8DE;
  text-align: center;
  cursor: pointer;
  font-family: "Inter", system-ui, sans-serif;
  transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
  animation: cardIn 0.32s ease-out both;
  isolation: isolate;
  min-height: 158px;
}

@media (max-width: 899px) {
  .card {
    min-height: 174px;
    padding: 12px 7px 9px;
    border-radius: 15px;
  }
  .card :deep(.item-thumbnail) {
    width: min(25vw, 92px);
    height: min(25vw, 92px);
    border-radius: 20px;
  }
  .card__title { font-size: 12px; }
  .card__price, .card__status { max-width: 100%; padding-inline: 8px; font-size: 10px; white-space: nowrap; }
}
@keyframes cardIn {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(98, 88, 217, 0.14);
}

.card--rare { border-color: #3F8EE6; }
.card--epic { border-color: #A855F7; }
.card--legendary {
  border-color: #F5A524;
  background: linear-gradient(140deg, #fff 0%, #fff8e6 100%);
  overflow: hidden;
}
.card--legendary::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 14px;
  background: linear-gradient(120deg, transparent 30%, rgba(245, 165, 36, 0.18) 50%, transparent 70%);
  background-size: 200% 200%;
  animation: shine 2.6s linear infinite;
  pointer-events: none;
  z-index: 0;
}
@keyframes shine {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.card--equipped {
  background: linear-gradient(140deg, #f0fff4 0%, #fff 100%);
  border-color: #22c55e;
}

.card--selected {
  transform: translateY(-2px);
  box-shadow: 0 0 0 3px rgba(98, 88, 217, 0.35), 0 12px 26px rgba(98, 88, 217, 0.2);
  border-color: #6258D9;
}
.card--selected.card--equipped {
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.4), 0 12px 26px rgba(34, 197, 94, 0.25);
  border-color: #22c55e;
}

.card__badge {
  position: absolute;
  top: 6px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.6px;
  padding: 3px 7px;
  border-radius: 6px;
  z-index: 2;
  color: #fff;
}
.card__badge--new { left: 6px; background: #6258D9; }
.card__badge--worn { right: 6px; background: #22c55e; }
.card__badge--owned {
  right: 6px;
  background: #6258D9;
  width: 18px;
  height: 18px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 11px;
}

.card :deep(.item-thumbnail) { margin: 4px 0 6px; position: relative; z-index: 1; }

.card__title {
  font-size: 12px;
  font-weight: 700;
  color: #292B33;
  line-height: 1.2;
  position: relative;
  z-index: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 28px;
}

.card__footer {
  margin-top: auto;
  padding-top: 6px;
  width: 100%;
  display: flex;
  justify-content: center;
  position: relative;
  z-index: 1;
}

.card__price {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #FFF5E1;
  border-radius: 999px;
  padding: 3px 10px;
  font-weight: 700;
  font-size: 12px;
  color: #B57A00;
}
.card__price--lock {
  background: #FFEEE6;
  color: #6258D9;
}

.card__status {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 10px;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.3px;
  text-transform: none;
}
.card__status--owned {
  background: #E8E5F4;
  color: #6258D9;
}
.card__status--equipped {
  background: #DCFCE7;
  color: #15803d;
}
</style>
